"use client";

import Image from "next/image";
import {formatPrice} from "@/utils/format-price";
import {formatDate} from "@/utils/format-date";
import Link from "next/link";
import {VeicoloDataVeicolo} from "@/interface/VeicoloDataVeicolo";
import React from "react";
import { CalendarDays, Gauge, Fuel, Settings2, Palette, MapPin, ArrowRight } from "lucide-react";
import { canOptimizeImageSource, getDisplayImageSource } from "@/lib/image-source";
import { shouldShowNetPriceForCommercialVehicle } from "@/utils/commercial-vehicle";


export interface Product {
    product: VeicoloDataVeicolo
    priority?: boolean;
    fallbackImage?: string;
    onlineCoupon?: { active: boolean; titolo: string; note?: string; discount: number } | null;
    compact?: boolean;
    onActionClick?: () => void;
}

export default function ProductCard({
    product,
    priority = false,
    fallbackImage = "/autoinarrivo.webp",
    onlineCoupon = null,
    compact = false,
    onActionClick,
}: Product) {
    const imageSrc = getDisplayImageSource(product.foto, fallbackImage, {
        slug: product.slug,
        articolo: product.articolo,
    });
    const isOptimizableImage = canOptimizeImageSource(imageSrc);
    const couponNote = onlineCoupon?.note?.trim() || "";
    const showOnlineCoupon = !!onlineCoupon?.active && onlineCoupon.discount < 0 && couponNote.length > 0;
    const imageHeightClass = compact ? "h-[11rem]" : "h-[15rem]";
    const imageSize = compact ? 320 : 400;
    const showNetPrice = shouldShowNetPriceForCommercialVehicle(
        product.categoria || product.tipoVeicolo,
        product.codiceIVA
    );

    return (

        <div className="relative bg-white border border-gray-100 noleggioCard rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">

            <Link
                hrefLang="it"
                id={`${product.slug}-image`}
                title={product.marca + ' ' + product.modello}
                href={`/parco-veicoli/${product.slug}`}
                aria-label={`Scopri ${product.marca} ${product.modello}`}
                className={`image-container relative block ${imageHeightClass}`}
            >

                {
                    new Date(product.immatricolazione) > new Date() && (
                        <div className="z-10 text-[10px] font-bold rounded p-1 px-2 absolute top-2 left-2 bg-primary text-white border-0 shadow-sm">
                            {`IN ARRIVO ENTRO ${formatDate(new Date(product.immatricolazione))}`}
                        </div>
                    )
                }
                {showOnlineCoupon && (
                    <div className="z-10 text-[10px] font-bold rounded py-1 px-2 absolute bottom-2 right-2 bg-green-600 text-white border-0 shadow-sm">
                        {couponNote}
                    </div>
                )}

                <Image
                    src={imageSrc}
                    alt={product.marca + ' ' + product.modello + ' ' + product.articolo}
                    className={`w-full object-cover ${imageHeightClass}`}
                    width={imageSize}
                    height={compact ? 220 : 300}
                    priority={priority}
                    loading={priority ? "eager" : "lazy"}
                    sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                    quality={70}
                    placeholder={'blur'}
                    blurDataURL="/autoinarrivo.webp"
                    unoptimized={!isOptimizableImage}
                />
            </Link>

            <div className={`relative px-4 ${compact ? "py-2 pb-3" : "py-2 pb-4"}`}>
                
                <div className={`flex justify-between items-start ${compact ? "pt-3 pb-1.5" : "pt-4 pb-2"}`}>
                    <div className="max-w-[60%]">
                        <h3 className={`font-bold text-gray-900 leading-tight ${compact ? "text-base" : "text-lg"}`}>{product.marca}</h3>
                        <h4 className={`font-medium text-gray-500 mt-0.5 pr-2 leading-snug ${compact ? "text-[10px]" : "text-[11px]"}`}>{product.modello}</h4>
                    </div>

                    <div className="text-right">
                        <p className={`${compact ? "text-base" : "text-lg"} font-black text-primary`}>
                            {showNetPrice
                                ? formatPrice((+product.prezzo) / 1.22)
                                : formatPrice(+product.prezzo)}
                        </p>
                        {showNetPrice && (
                            <span className="text-[9px] font-bold text-gray-400 block -mt-1">+ IVA</span>
                        )}
                    </div>
                </div>

                {/* Grid Spec - 3 columns, Horizontal items (Icon + Text) */}
                <div className={`grid grid-cols-3 gap-x-1 border-t border-gray-50 ${compact ? "gap-y-2 py-3" : "gap-y-3 py-4"}`}>
                    <div className="flex items-center gap-1.5 justify-start overflow-hidden">
                        <CalendarDays className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} text-gray-400 shrink-0`} />
                        <p className={`${compact ? "text-[9px]" : "text-[10px]"} font-semibold text-gray-600 truncate`}>{formatDate(product.immatricolazione)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 justify-start overflow-hidden">
                        <Gauge className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} text-gray-400 shrink-0`} />
                        <p className={`${compact ? "text-[9px]" : "text-[10px]"} font-semibold text-gray-600 truncate`}>{product.km ? product.km + ' km' : '-'}</p>
                    </div>
                    <div className="flex items-center gap-1.5 justify-start overflow-hidden">
                        <Fuel className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} text-gray-400 shrink-0`} />
                        <p className={`${compact ? "text-[9px]" : "text-[10px]"} font-semibold text-gray-600 truncate`}>{product.alimentazione ?? '-'}</p>
                    </div>
                    <div className="flex items-center gap-1.5 justify-start overflow-hidden">
                        <Settings2 className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} text-gray-400 shrink-0`} />
                        <p className={`${compact ? "text-[9px]" : "text-[10px]"} font-semibold text-gray-600 truncate`}>{product.cambio ?? '-'}</p>
                    </div>
                    <div className="flex items-center gap-1.5 justify-start overflow-hidden">
                        <Palette className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} text-gray-400 shrink-0`} />
                        <p className={`${compact ? "text-[9px]" : "text-[10px]"} font-semibold text-gray-600 truncate`}>{product.colore ?? '-'}</p>
                    </div>
                    <div className="flex items-center gap-1.5 justify-start overflow-hidden">
                        <MapPin className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} text-gray-400 shrink-0`} />
                        <p className={`${compact ? "text-[9px]" : "text-[10px]"} font-semibold text-gray-600 truncate`}>{product.provincia ?? '-'}</p>
                    </div>
                </div>

                {product.flagArrivo == 1 && (
                    <div className={`absolute ${showOnlineCoupon ? "bottom-8" : "bottom-2"} right-4 bg-gray-50/80 px-2 py-0.5 rounded text-[9px] font-bold text-gray-400`}>
                        IN ARRIVO A {product.provincia?.toUpperCase()}
                    </div>
                )}

                <div className={`${compact ? "mt-3 h-[44px]" : "mt-4 h-[56px]"}`}>
                    <Link hrefLang="it" id={product.slug} title={product.marca + ' ' + product.modello} href={`/parco-veicoli/${product.slug}`}>
                        <button
                            onClick={onActionClick}
                            className={`show-on-hover text-white font-bold m-0 bg-gradient-to-r from-[#000D7D] to-[#001AFF] rounded-lg flex items-center justify-center w-full h-full shadow-lg hover:shadow-primary/30 transition-all ${compact ? "gap-2 text-sm" : "gap-3"}`}
                            aria-label={`Scopri ${product.marca} ${product.modello}`}>
                            Scopri
                            <ArrowRight className={compact ? "h-4 w-4" : "h-5 w-5"} />
                        </button>
                    </Link>
                </div>

            </div>

        </div>

    )

}
